import BucketCacheService from "../../infrastructure/Services/BucketCacheService";

/**
 * Lectura y escritura del caché de pistas. Vive fuera de `PlaylistTrack`
 * porque `Playlist.set` precachea la lista con EXACTAMENTE el mismo criterio:
 * tenerlo duplicado es cómo el arreglo del lector se aplicó en una sola de las
 * dos puntas.
 */

/**
 * URL sin query ni fragmento. Una pista puesta por el usuario puede traer
 * parámetros firmados o tokens en la query; eso no tiene por qué quedar en los
 * logs del renderer ni en los del escritorio, que se adjuntan a los reportes.
 */
export const safeUrlForLog = (url: string): string => {
  try {
    const parsed = new URL(url);

    // `origin` es el string "null" en cualquier esquema que no sea de los
    // "especiales" del WHATWG — y `eriol-workspace://` es el caso MAYORITARIO
    // acá. Usarlo a secas imprimía "null/artists/…" y se comía el id del
    // workspace, justo el dato con el que se diagnostica un fallo de caché.

    const origin =
      parsed.origin === "null"
        ? `${parsed.protocol}//${parsed.host}`
        : parsed.origin;

    return `${origin}${parsed.pathname}`;
  } catch {
    return url.split(/[?#]/)[0];
  }
};

/**
 * Tipo de audio por extensión. Lo comparten los TRES lugares que arman el blob
 * de una pista —el precacheo de la lista, la lectura del workspace y la bajada
 * remota—, porque un blob SIN `type` es una pista que el <audio> puede
 * rechazar con "formato no soportado" aunque los bytes estén sanos: la fuente
 * de un `blob:` es el `type` del Blob, no hay headers de los que sacarlo.
 */
const AUDIO_TYPES: Record<string, string> = {
  webm: "audio/webm",

  mp3: "audio/mpeg",

  m4a: "audio/mp4",

  ogg: "audio/ogg",

  wav: "audio/wav",

  aac: "audio/aac",

  flac: "audio/flac",
};

export const audioTypeFromPath = (path: string): string | undefined => {
  const ext = path.split(/[?#]/)[0].split(".").pop()?.toLowerCase();

  return (ext && AUDIO_TYPES[ext]) || undefined;
};

/**
 * Guardar en la Cache API es best-effort: si no hay storage disponible (cuota
 * llena, modo privado, permisos) la pista igual se reproduce desde el blob que
 * ya está en memoria. Antes este `put` se esperaba ANTES de crear la URL del
 * blob y su fallo se tragaba el catch de arriba, así que una descarga perfecta
 * terminaba salteada por "fuente remota sin camino de reproducción".
 */
export const cacheBestEffort = async (
  key: string,
  blob: Blob,
  contentType?: string,
) => {
  try {
    await BucketCacheService.put(key, blob, contentType);
  } catch (error) {
    console.warn(
      "[PlaylistCache] No se pudo cachear la pista; sigue igual:",

      safeUrlForLog(key),

      error,
    );
  }
};

/**
 * Sacar una entrada es igual de best-effort. Se usa cuando la pista que salió
 * de esa copia NO se pudo reproducir: una respuesta 200 que es una página HTML
 * de error, o un codec que el <audio> no soporta, guardada bajo la URL de la
 * fuente, hace que todo intento posterior salga del caché y falle igual —para
 * siempre, aunque el servidor ya se haya recuperado—. Desalojarla es lo que
 * deja que el próximo intento vuelva a la fuente.
 */
export const evictBestEffort = async (key: string) => {
  try {
    await BucketCacheService.delete(key);
  } catch (error) {
    console.warn(
      "[PlaylistCache] No se pudo desalojar la pista del caché:",

      safeUrlForLog(key),

      error,
    );
  }
};

/**
 * Leerlo es tan best-effort como guardarlo: abrir la Cache API TIRA (no
 * devuelve `undefined`) cuando el almacenamiento no está disponible —
 * particionado, modo privado, permisos—, y esa excepción caía al catch general
 * de `play()`. El control nunca llegaba entonces al camino de bajada, así que
 * una fuente remota perfectamente sana terminaba rechazada por "sin camino de
 * reproducción" en el primer renderer sin storage. Un fallo acá solo significa
 * "no hay copia": se sigue a la fuente.
 *
 * Una entrada vacía tampoco cuenta como copia: reproducirla es un <audio> mudo,
 * y bajarla de nuevo es lo que corresponde.
 */
export const cachedBlob = async (key: string): Promise<Blob | undefined> => {
  try {
    const hit = await BucketCacheService.match(key);

    if (!hit) return undefined;

    const blob = await hit.blob();

    return blob.size > 0 ? blob : undefined;
  } catch (error) {
    console.warn(
      "[PlaylistCache] No se pudo leer el caché; se va a la fuente:",

      safeUrlForLog(key),

      error,
    );

    return undefined;
  }
};
